import { RefObject } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Card } from 'primereact/card'
import { Divider } from 'primereact/divider'
import { Messages } from 'primereact/messages'

import { PanelContainer } from '../../../components/Panel'
import { EmailChips } from './EmailChips'
import { FormFieldName } from './EmailForm.container'

type Props = {
  formik: FormikProps<any>
  panelId: string
  separator: string
  messagesRef: RefObject<Messages>
  isFormFieldInvalid: (name: FormFieldName) => boolean
  wrongInboundEmails: string[] | null
  wrongOutboundEmails: string[] | null
  wrongSupportEmails: string[] | null
  getFormErrorMessage: (name: FormFieldName) => JSX.Element
  isEdit: (idToCheck: string) => boolean
  isLoading: boolean
  isRequestRunning?: boolean
  hasChangesPending?: boolean
}

export const EmailForm: React.FC<Props> = ({
  formik,
  panelId,
  separator,
  messagesRef,
  isFormFieldInvalid,
  wrongInboundEmails,
  wrongOutboundEmails,
  wrongSupportEmails,
  getFormErrorMessage,
  isEdit,
  isLoading,
  isRequestRunning = false,
  hasChangesPending = false,
}) => {
  const { t } = useTranslation()

  return (
    <PanelContainer
      header={t('settings.email.title')}
      formik={formik}
      id={panelId}
      toggleable={false}
      isRequestRunning={isRequestRunning}
      hasChangesPending={hasChangesPending}
    >
      <div className="grid">
        <div className="col-12 lg:col-4">
          <h3 className="m-0">{t('settings.email.notifications.title')}</h3>
          <p>{t('settings.email.notifications.subtitle')}</p>
        </div>
        <div className="col-12 lg:col-7 lg:col-offset-1">
          <Card className="md:px-4">
            <Messages ref={messagesRef} />

            {/* Inbound */}
            <EmailChips
              id="inbound"
              label={t('settings.email.notifications.inbound.label')}
              value={formik.values.inbound}
              separator={separator}
              formik={formik}
              isInvalid={isFormFieldInvalid('inbound')}
              errorMessage={getFormErrorMessage('inbound')}
              wrongEmails={wrongInboundEmails}
              disabled={!isEdit(panelId)}
              isLoading={isLoading}
            />

            {/* Outbound */}
            <EmailChips
              id="outbound"
              label={t('settings.email.notifications.outbound.label')}
              value={formik.values.outbound}
              separator={separator}
              formik={formik}
              isInvalid={isFormFieldInvalid('outbound')}
              errorMessage={getFormErrorMessage('outbound')}
              wrongEmails={wrongOutboundEmails}
              disabled={!isEdit(panelId)}
              isLoading={isLoading}
            />
          </Card>
        </div>

        <div className="col-12">
          <Divider />
        </div>

        <div className="col-12 lg:col-4">
          <h3 className="m-0">{t('settings.email.support.title')}</h3>
          <p>{t('settings.email.support.subtitle')}</p>
        </div>
        <div className="col-12 lg:col-7 lg:col-offset-1">
          <Card className="md:px-4">
            {/* Support */}
            <EmailChips
              id="support"
              label={t('settings.email.support.support.label')}
              value={formik.values.support}
              separator={separator}
              formik={formik}
              isInvalid={isFormFieldInvalid('support')}
              errorMessage={getFormErrorMessage('support')}
              wrongEmails={wrongSupportEmails}
              disabled={!isEdit(panelId)}
              isLoading={isLoading}
            />
          </Card>
        </div>
      </div>
    </PanelContainer>
  )
}
